import {
  dot,
  Fn,
  float,
  If,
  max,
  mix,
  mx_noise_float,
  mx_noise_vec3,
  sin,
  smoothstep,
  sqrt,
  step,
  vec3,
  vec4,
} from 'three/tsl';
import type { TerrainProfile } from './TerrainProfile';

type N = any;

/**
 * Noise evaluated only where `weight` > 0: a real branch, so fragments that would
 * multiply the result by zero (far away, dry land...) skip the work. 0 elsewhere.
 */
export const noiseWhen = Fn(([p, weight]: N[]) => {
  const v = float(0).toVar();
  If(weight.greaterThan(0.0001), () => {
    v.assign(mx_noise_float(p));
  });
  return v;
});

/** Vector noise evaluated only where `weight` > 0 (see noiseWhen). */
export const noise3When = Fn(([p, weight]: N[]) => {
  const v = vec3(0).toVar();
  If(weight.greaterThan(0.0001), () => {
    v.assign(mx_noise_vec3(p));
  });
  return v;
});

/** Any vec3 expression, built and evaluated only where `weight` > 0. */
export const vec3When = (build: () => N, weight: N): N =>
  Fn(() => {
    const v = vec3(0).toVar();
    If(weight.greaterThan(0.0001), () => {
      v.assign(build());
    });
    return v;
  })();

/** Per-fragment inputs a terrain style can use (all TSL nodes). */
export interface SurfaceContext {
  /** Hollow-local position on the ground (m). */
  p: N;
  /** Height above the water line (m); < 0 under water. */
  h: N;
  /** 0 = flat … 1 = vertical. */
  slope: N;
  /** Local up (toward the hollow centre). */
  up: N;
  /** Distance to the camera (m). */
  dist: N;
  /** Close-up detail weight (1 near, 0 beyond ~220 m). */
  near: N;
  /** Mid-distance detail weight (1 near, 0 beyond ~2.5 km). */
  mid: N;
  /**
   * Ground one pixel spans here (m): the side of a square with the area the pixel covers.
   * Palettes fade noise finer than a few pixels toward its mean (see `resolved`). Left out,
   * every noise term is drawn at full strength at any distance.
   */
  footprint?: N;
  /** In a world of several biomes: their weights here (vec4; see BiomeField). */
  biome?: N;
  /** The noise terms the tiles carry (TerrainStyle.bakedNoise), by bakeKey: noiseTerm reads them. */
  baked?: Map<string, N>;
  /**
   * With erosion: where the ground lies between its gullies and ridges (vec3, each −1 at a
   * gully's bottom … 1 on a crest, 0 on flat ground): x for gullies of every size, y for the
   * narrow ones only. Resolved as finely as the ground's tiles are (coarser far away). z, the
   * relief: −1 deep in a valley … 1 on a spur or summit, against the land's shape at the scale
   * of whole valleys and ranges (TileGenerator).
   */
  ridge?: N;
  profile: TerrainProfile;
  /** Seconds since start (for moving surfaces: water, lava). */
  time: N;
}

/**
 * How much of a pattern `wavelength` metres across a pixel resolves: all of it while the
 * pattern spans 4 pixels or more, none below 1.5. Finer noise turns into per-pixel grain that
 * TRAA only partly averages out, so palettes fade it toward its mean. Without a footprint
 * this is the number 1, and callers build the same nodes as before.
 */
export function resolved(ctx: Pick<SurfaceContext, 'footprint'>, wavelength: N | number): N | 1 {
  if (ctx.footprint === undefined) return 1;
  return smoothstep(1.5, 4, float(wavelength).div(ctx.footprint));
}

/** A noise term: its value `n`, and how much of it the pixel resolves (`a`, see `resolved`). */
export interface NoiseTerm {
  /** mx_noise_float: mean 0, sd 0.265. Where `a` is 0 it isn't evaluated and reads 0. */
  n: N;
  a: N | 1;
}

/**
 * A noise term a style has the tiles carry (TerrainStyle.bakedNoise): the tile generator works it
 * out once per texel, and the palette reads it back instead of working it out for every pixel of
 * every frame. Broad terms (hundreds of metres and up) lose nothing to the tiles' texels; a world
 * whose coarsest tiles are too coarse for a term keeps working it out per pixel (bakeable).
 */
export interface BakedNoise {
  wavelength: number;
  offset?: [number, number, number];
  /** A term of the forest mask (a wrapper that resizes the forests scales it). */
  forest?: boolean;
}

/** How noiseTerm finds a baked term (SurfaceContext.baked). */
export const bakeKey = (wavelength: number, offset?: [number, number, number]): string =>
  `${wavelength}|${offset ? offset.join(',') : ''}`;

/**
 * Noise with features about `wavelength` metres across, fading where a pixel can't show them.
 * At the surface point itself (`ctx.p`), a term the tiles carry (SurfaceContext.baked) is read
 * from them instead: the same noise, worked out once per tile texel.
 */
export function noiseTerm(
  ctx: Pick<SurfaceContext, 'footprint'> & { p?: N; baked?: Map<string, N> },
  p: N,
  wavelength: number,
  offset?: [number, number, number],
  /** Only worked out where this is above 0 (it reads 0 elsewhere): where the term can matter. */
  when?: N,
): NoiseTerm {
  const a = resolved(ctx, wavelength);
  const baked = p === ctx.p ? ctx.baked?.get(bakeKey(wavelength, offset)) : undefined;
  if (baked) return { n: baked, a };
  let x: N = p.mul(1 / wavelength);
  if (offset) x = x.add(vec3(...offset));
  const weight: N = when === undefined ? a : a === 1 ? when : when.mul(a);
  return { n: weight === 1 ? mx_noise_float(x) : noiseWhen(x, weight), a };
}

/**
 * A plain noise (no fading), `wavelength` metres across, read from the tiles when they carry it
 * (TerrainStyle.bakedNoise) and the surface point is the one asked about: for palettes that
 * don't fade their terms. Without a context it is worked out here.
 */
export function broadNoise(
  ctx: { p?: N; baked?: Map<string, N> } | undefined,
  p: N,
  wavelength: number,
  offset?: [number, number, number],
): N {
  const baked = ctx && p === ctx.p ? ctx.baked?.get(bakeKey(wavelength, offset)) : undefined;
  if (baked) return baked;
  let x: N = p.mul(1 / wavelength);
  if (offset) x = x.add(vec3(...offset));
  return mx_noise_float(x);
}

/** A term used as an offset: its mean is 0, so fading it is a matter of scaling it down. */
export const linear = (t: NoiseTerm): N => (t.a === 1 ? t.n : t.n.mul(t.a));

/**
 * `value`, a nonlinear function of a term (a threshold, a colour ramp), fading to its mean
 * as the term fades. Keeps the pixel's average colour: E[smoothstep(0.02, 0.28, n)] is 0.30,
 * where thresholding the faded noise (0) would give 0 and the forests would vanish.
 */
export const faded = (t: NoiseTerm, mean: N | number, value: N): N =>
  t.a === 1 ? value : mix(mean, value, t.a);

/**
 * Means of the Verdant palette's nonlinear noise terms over mx_noise_float's values (checked by
 * TerrainStyle.test.ts against a CPU copy of the noise). Its values are bell-shaped around 0
 * with an sd of 0.265, so thresholds above 0 pass only a small share.
 */
export const NOISE_MEANS = {
  /** smoothstep(0.02, 0.28, n): forest cover. */
  forest: 0.301,
  /** smoothstep(0.18, 0.42, n): autumn regions. */
  autumn: 0.143,
  /** smoothstep(0.2, 0.5, n): flower fields. */
  field: 0.107,
  /** smoothstep(-0.25, 0.05, n) and its product with smoothstep(0.1, 0.4, n): field colours. */
  fieldKind: [0.634, 0.192] as [number, number],
  /** smoothstep(0.35, 0.75, 0.6 n₁ + 0.4 n₂), two independent noises: dry grass (rare). */
  dry: 0.0033,
  /** cos(6 n) and cos(1.5 n): how much of the rock bands' contrast is left once the noise in their phase is gone. */
  bandPhase: [0.239, 0.923] as [number, number],
  /** smoothstep(-0.1, 0.15, n): the stretches of coast that have beaches. */
  beach: 0.466,
};

export interface LandSurface {
  albedo: N;
  roughness: N;
  /** Light the ground gives off by itself (bioluminescence, lava…). */
  emissive?: N;
  /**
   * How much of each detail material the ground here is (vec4: ground, rock, sand, snow; see
   * TerrainDetail): textures that add small-scale variation and relief close by. Left out:
   * ground, with rock where it's steep.
   */
  detail?: N;
}

export interface WaterStyle {
  shallow: [number, number, number];
  deep: [number, number, number];
  /** e-folding depth of the colour change (m). */
  depthScale: number;
  /** Colour of the shoreline foam (or crust). */
  foam?: [number, number, number];
  /** Surface roughness (default: a calm, glossy 0.06). */
  roughness?: number;
  /** Optional glow in the water. */
  emissive?: (ctx: SurfaceContext) => N;
}

/**
 * How a hollow's ground looks: a palette function over height, slope and noise.
 * The same function colours the ground at your feet and the far side overhead, so
 * the two always agree.
 */
export interface TerrainStyle {
  land(ctx: SurfaceContext): LandSurface;
  water: WaterStyle;
  /**
   * 0..1 forest cover (shared with the tree renderer so trees match the canopy colour). The
   * terrain passes its footprint; trees, drawn close by, don't.
   */
  forestMask(
    ctx: Pick<SurfaceContext, 'p' | 'h' | 'slope' | 'profile' | 'footprint' | 'biome' | 'ridge' | 'baked'>,
  ): N;
  /**
   * Broad noise terms its palette uses at the surface point (as noiseTerm(ctx, ctx.p, …) calls,
   * with the same wavelength and offset), for the tiles to carry: up to 8. Each saves a noise for
   * every pixel of ground (a few tenths of a millisecond each at 1440p).
   */
  bakedNoise?: BakedNoise[];
}

/** Temperate forests with a snow line: the default look. */
/**
 * Regions where broadleaf forests have turned (0..1), shared by terrain and 3D trees. With a
 * footprint (the terrain's) it fades to its mean where the regions are too small to show.
 */
export const verdantAutumn = (p: N, ctx: Pick<SurfaceContext, 'footprint'> = {}): N => {
  const t = noiseTerm(ctx, p, 3800, [2.7, 9.4, 5.1]);
  return faded(t, NOISE_MEANS.autumn, smoothstep(0.18, 0.42, t.n));
};
/** Autumn crown colours (orange … red). */
export const AUTUMN: [[number, number, number], [number, number, number]] = [
  [0.3, 0.15, 0.035],
  [0.26, 0.07, 0.03],
];

/**
 * Detail weights (see LandSurface.detail) from a palette's masks, applied in this order: sand
 * over the ground, rock over that, snow over everything. `ground` names the material the ground
 * itself is (0 ground, 2 sand, 3 snow: a desert's ground is sand, a snowfield's snow).
 */
export function detailWeights(sand: N, rock: N, snow: N, ground: 0 | 2 | 3 = 0): N {
  const keep = float(1).sub(snow);
  const r = rock.mul(keep);
  const s = sand.mul(float(1).sub(rock)).mul(keep);
  const g = float(1).sub(sand).mul(float(1).sub(rock)).mul(keep);
  if (ground === 2) return vec4(0, r, s.add(g), snow);
  if (ground === 3) return vec4(0, r, s, snow.add(g));
  return vec4(g, r, s, snow);
}

/**
 * Where the ground lies between its gullies and ridges (see SurfaceContext.ridge): `all` for
 * every size, `narrow` for the narrow ones; `relief` where it stands above or below the shape
 * of its valleys and ranges. 0 without erosion.
 */
export const ridgeOf = (ctx: Pick<SurfaceContext, 'ridge'>): { all: N; narrow: N; relief: N } =>
  ctx.ridge
    ? { all: ctx.ridge.x, narrow: ctx.ridge.y, relief: ctx.ridge.z }
    : { all: float(0), narrow: float(0), relief: float(0) };

/** Verdant's forests: patches `wavelength` metres across, off steep slopes and the snow. */
export const verdantForest =
  (wavelength = 2300): TerrainStyle['forestMask'] =>
  (ctx) => {
    const { p, h, slope, profile } = ctx;
    const forestN = noiseTerm(ctx, p, wavelength, [3.1, 7.7, 1.3]);
    // Ragged edges: finer noise bends the border in and out, as the edges of real woods run.
    let n: N = forestN.n.add(linear(noiseTerm(ctx, p, wavelength * 0.26, [8.2, 1.9, 4.4])).mul(0.45));
    // Forests creep up the gullies, where water gathers, and thin out along the crests.
    if (ctx.ridge) n = n.add(ridgeOf(ctx).all.mul(-0.2));
    n = n.add(forestEdge(ctx, p, wavelength * 0.065, [2.6, 6.1, 9.8], n));
    return faded(forestN, NOISE_MEANS.forest, smoothstep(0.02, 0.28, n))
      .mul(float(1).sub(smoothstep(0.2, 0.4, slope)))
      .mul(float(1).sub(smoothstep(profile.snowLine * 0.55, profile.snowLine * 0.8, h)));
  };

/**
 * A forest border's finest raggedness (±0.22, added to `n`, which is thresholded 0.02 … 0.28):
 * it can only move the border where `n` lies within its reach of that band, so it is worked
 * out only there (well inside a wood or out in the open it changes nothing).
 */
export function forestEdge(
  ctx: Pick<SurfaceContext, 'footprint'> & { p?: N; baked?: Map<string, N> },
  p: N,
  wavelength: number,
  offset: [number, number, number],
  n: N,
): N {
  const near = step(-0.2, n).mul(step(n, 0.5));
  return linear(noiseTerm(ctx, p, wavelength, offset, near)).mul(0.22);
}

/** Rock strata: one band per this many metres of (palette) height. */
const BAND_HEIGHT = (2 * Math.PI) / 0.21;
/** Their contrast: faint (bold bands drew contour lines over every slope). */
const STRATA = 0.14;
/**
 * How much darker the floor of a valley is (see SurfaceContext.ridge's relief): its walls hide
 * some of the far side's light. (Summits aren't lightened: snowy ones went white.)
 */
export const RELIEF_SHADE = 0.24;

/** The colour factor for a place's relief (−1 deep in a valley … 1 on a summit). */
export const reliefShade = (relief: N): N =>
  float(1).sub(smoothstep(0, 0.6, relief.negate()).mul(RELIEF_SHADE));

/**
 * Rock strata, about 0.5 ± STRATA (mix a palette's dark and light rock by it): layers along the
 * height, broken by the palette's 95 m and 11 m noise terms. Two waves out of step make layers
 * of uneven thickness, and a slower one makes runs of bold and faint ones: one sine's even
 * stripes read as streaks across every smooth face. With a footprint each wave fades where its
 * layers lie too close together to show, and as the noise in its phase fades.
 */
export function strata(
  ctx: Pick<SurfaceContext, 'h' | 'slope' | 'profile' | 'footprint'>,
  fineT: NoiseTerm,
  patchT: NoiseTerm,
): N {
  const { h, slope, profile } = ctx;
  const fine = linear(fineT);
  const phase = h.mul(0.21).add(fine.mul(6)).add(linear(patchT).mul(1.5));
  const run = sin(h.mul(0.21 * 0.29).add(fine.mul(1.5)))
    .mul(0.45)
    .add(0.55);
  let c1: N = float(STRATA);
  let c2: N = float(STRATA);
  if (ctx.footprint !== undefined) {
    // On a slope the layers lie BAND_HEIGHT / sin(θ) apart along the ground; closer than a few
    // pixels they average to grey. Faded noise in their phase leaves them less contrast
    // (E[cos(k n)] of each term; the second wave's, its square as an estimate).
    const sinSlope = sqrt(slope.mul(float(2).sub(slope)));
    const spacing = float(BAND_HEIGHT * (profile.heightScale ?? 1)).div(max(sinSlope, 1e-3));
    const [cFine, cPatch] = NOISE_MEANS.bandPhase;
    const left = mix(float(cFine), float(1), fineT.a).mul(mix(float(cPatch), float(1), patchT.a));
    c1 = float(resolved(ctx, spacing)).mul(left).mul(STRATA);
    c2 = float(resolved(ctx, spacing.div(2.37)))
      .mul(left)
      .mul(left)
      .mul(STRATA);
  }
  return sin(phase)
    .mul(c1.mul(0.65))
    .add(sin(phase.mul(2.37).add(1.3)).mul(c2.mul(0.35)))
    .mul(run)
    .add(0.5);
}

/**
 * Where a shore has a beach (0..1): on gentle ground, in stretches a few hundred metres long,
 * with grass or rock meeting the water between them. Sand round every coast drew a bright
 * outline round every lake and island seen from across the world.
 */
export function beaches(ctx: Pick<SurfaceContext, 'p' | 'slope' | 'footprint'>): N {
  const t = noiseTerm(ctx, ctx.p, 600, [7.3, 2.9, 5.6]);
  return faded(t, NOISE_MEANS.beach, smoothstep(-0.1, 0.15, t.n)).mul(
    float(1).sub(smoothstep(0.06, 0.16, ctx.slope)),
  );
}

/** Clumps of crowns in a forest's canopy seen from afar: their size (m), how far off they still
 * show without a pixel footprint to go by (m), and how much brighter (or darker in the gaps). */
const CROWN_SIZE = 50;
const CROWN_FAR = 12000;
const CROWN_CONTRAST = 1.1;

/**
 * A forest's canopy from afar is no flat paint: sunlit clumps of crowns and the shaded gaps
 * between them stipple it. A brightness factor about 1 for the canopy's colour, only evaluated
 * where `forest` (its cover) is above 0, fading where a clump shrinks below a few pixels.
 */
export function canopyCrowns(ctx: Pick<SurfaceContext, 'p' | 'dist' | 'footprint'>, forest: N): N {
  // Gone by CROWN_FAR, footprint or not, and softer from 5 km: from across the world a bright
  // canopy (autumn's) read as leopard spots.
  const near: N = float(1).sub(smoothstep(CROWN_FAR * 0.5, CROWN_FAR, ctx.dist));
  const shown: N = ctx.footprint === undefined ? near : near.mul(resolved(ctx, CROWN_SIZE));
  const w = forest.mul(shown);
  const contrast = mix(float(CROWN_CONTRAST), float(CROWN_CONTRAST * 0.6), smoothstep(5000, 10000, ctx.dist));
  return noiseWhen(ctx.p.mul(1 / CROWN_SIZE).add(vec3(4.4, 0.3, 7.1)), w)
    .mul(w.mul(contrast))
    .add(1);
}

/**
 * The narrow gullies' dark creases (a factor for the ground's colour). From across the world
 * the finest creases a tile holds are a pixel or two wide, and drawn on every slope they made
 * the far side look combed: they fade to a third of their depth by 12 km (the broad gullies and
 * crests still draw the landform).
 */
export function creaseShade(ctx: Pick<SurfaceContext, 'dist'>, narrow: N, depth = 0.28): N {
  const far = smoothstep(4000, 12000, ctx.dist).mul(0.67);
  return float(1).sub(smoothstep(0.15, 0.8, narrow.negate()).mul(float(1).sub(far).mul(depth)));
}

/** Rock streaks: their width (m), how many times longer they run, and how much they vary. */
const STREAK_WIDTH = 6;
const STREAK_STRETCH = 10;
const STREAK_SHADE = 0.55;

/**
 * Streaks down steep rock, as water stains and scree runs draw them on real faces: a brightness
 * factor about 1. The noise is stretched along the hollow's up, so on any steep face it runs
 * down the fall line (the sun is overhead everywhere, so up is the same for every face). Only
 * worked out where `rock` is, from 20 m (closer, the rock texture's own detail) until the
 * streaks shrink to a few pixels (by 3.5 km, or by the footprint where a world filters).
 */
export function rockStreaks(ctx: Pick<SurfaceContext, 'p' | 'up' | 'dist' | 'footprint'>, rock: N): N {
  const range: N =
    ctx.footprint === undefined
      ? float(1).sub(smoothstep(1500, 3500, ctx.dist))
      : float(resolved(ctx, STREAK_WIDTH));
  const w = rock.mul(range).mul(smoothstep(20, 60, ctx.dist));
  const q = ctx.p.sub(ctx.up.mul(dot(ctx.p, ctx.up).mul(1 - 1 / STREAK_STRETCH)));
  return noiseWhen(q.mul(1 / STREAK_WIDTH), w)
    .mul(w.mul(STREAK_SHADE))
    .add(1);
}

/** Alpine turf above the trees, and scree under the snow (linear RGB). */
const ALPINE_TURF: [number, number, number] = [0.2, 0.19, 0.1];
const SCREE: [number, number, number] = [0.23, 0.21, 0.18];

/**
 * Altitude zones, as real ranges show them from afar: above the tree line (from half the snow
 * line) the grass turns to pale, tawny alpine turf, and just under the snow to grey scree. `h`
 * is the palette height, jittered so the zones don't draw contour lines. Ground that isn't
 * grass (a desert's) only takes the scree (`turf` false).
 */
export function altitudeZones(grass: N, h: N, snowLine: number, turf = true): N {
  const scree = smoothstep(snowLine * 0.78, snowLine * 0.95, h);
  let g: N = grass;
  if (turf) g = mix(g, vec3(...ALPINE_TURF), smoothstep(snowLine * 0.5, snowLine * 0.72, h).mul(0.6));
  return mix(g, vec3(...SCREE), scree.mul(0.5));
}

/**
 * Above the trees, the crests of steep ground are scoured bare (0..1): ribs of rock between the
 * snow-filled gullies, as alpine faces look. `alpine` is 1 where that applies (high ground).
 */
export function rockRibs(crest: N, slope: N, alpine: N): N {
  return crest.mul(smoothstep(0.05, 0.16, slope)).mul(alpine);
}
/** Flower fields: heather, buttercups and daisies. */
const FIELD_COLS: [number, number, number][] = [
  [0.24, 0.14, 0.27],
  [0.4, 0.34, 0.09],
  [0.44, 0.45, 0.4],
];

export const VERDANT_STYLE: TerrainStyle = {
  forestMask: verdantForest(),
  // Its broad terms: forests, their ragged edges, autumn regions, the meadows' tone and hue,
  // flower fields and their colours, and where beaches run.
  bakedNoise: [
    { wavelength: 2300, offset: [3.1, 7.7, 1.3] },
    { wavelength: 2300 * 0.26, offset: [8.2, 1.9, 4.4] },
    { wavelength: 3800, offset: [2.7, 9.4, 5.1] },
    { wavelength: 1400 },
    { wavelength: 380 },
    { wavelength: 850, offset: [6.2, 1.4, 3.3] },
    { wavelength: 2600, offset: [0.4, 8.8, 2.1] },
    { wavelength: 600, offset: [7.3, 2.9, 5.6] },
  ],

  land(ctx) {
    const { p, h, slope, near, mid, profile, dist } = ctx;
    // Each term fades to its mean where a pixel spans too much ground to show it (the far side
    // of a big hollow): offsets through `linear`, thresholds and ramps through `faded`.
    const macroT = noiseTerm(ctx, p, 1400);
    const fineT = noiseTerm(ctx, p, 95);
    const patchT = noiseTerm(ctx, p, 11);
    const macro = linear(macroT);
    const fine = linear(fineT);
    const patch = linear(patchT);
    const grain = noiseWhen(p.mul(1 / 1.7), near).mul(near);
    // Forest cover first: the canopy's own noise is only worked out where there is forest.
    const cover = this.forestMask(ctx);
    const canopy = linear(noiseTerm(ctx, p, 160, undefined, cover)).mul(mid.mul(0.6).add(0.4));

    // Meadows drift between yellow-green and cooler blue-green at a mid scale.
    const hue = linear(noiseTerm(ctx, p, 380))
      .mul(0.5)
      .add(0.5);
    const lush = mix(vec3(0.075, 0.13, 0.03), vec3(0.05, 0.115, 0.04), hue);
    const meadow = mix(vec3(0.19, 0.22, 0.07), vec3(0.13, 0.185, 0.065), hue);
    const dry = vec3(0.3, 0.27, 0.13);
    let grass: N = mix(lush, meadow, macro.mul(0.5).add(0.5));
    const dryShare = faded(
      patchT,
      NOISE_MEANS.dry,
      smoothstep(0.35, 0.75, patchT.n.mul(0.6).add(fineT.n.mul(0.4))),
    );
    grass = mix(grass, dry, dryShare.mul(0.45));
    // Tussocks, thinner and lusher patches: from afar the meadows were one smooth blanket.
    // Without a pixel footprint they fade out with distance (by 6 km) instead.
    const tussocks =
      ctx.footprint === undefined ? patch.mul(float(1).sub(smoothstep(2500, 6000, dist))) : patch;
    grass = grass.mul(grain.mul(0.18).add(1)).mul(tussocks.mul(0.3).add(1));
    grass = altitudeZones(grass, h.add(fine.mul(40)), profile.snowLine);
    // Wildflowers: sparse speckles close to the camera.
    const flowerN = noiseWhen(p.mul(6.5), near);
    const flowerKind = noiseWhen(p.mul(0.13).add(vec3(9.1, 2.2, 4.4)), near);
    const flowerCol = mix(
      vec3(0.7, 0.58, 0.14),
      mix(vec3(0.7, 0.69, 0.72), vec3(0.4, 0.25, 0.58), flowerKind.mul(0.5).add(0.5)),
      smoothstep(-0.2, 0.2, flowerKind),
    );
    // Flower fields: broad drifts of heather, buttercups and daisies. They colour the
    // land as seen from across the hollow; up close they thicken the speckles.
    const fieldN = noiseTerm(ctx, p, 850, [6.2, 1.4, 3.3]);
    const fieldKind = noiseTerm(ctx, p, 2600, [0.4, 8.8, 2.1]);
    const [heather, buttercup, daisy] = FIELD_COLS;
    const [k1, k12] = NOISE_MEANS.fieldKind;
    const fieldCol = faded(
      fieldKind,
      vec3(...heather.map((c, i) => c * (1 - k1) + buttercup[i] * (k1 - k12) + daisy[i] * k12)),
      mix(
        vec3(...heather),
        mix(vec3(...buttercup), vec3(...daisy), smoothstep(0.1, 0.4, fieldKind.n)),
        smoothstep(-0.25, 0.05, fieldKind.n),
      ),
    );
    const field = faded(fieldN, NOISE_MEANS.field, smoothstep(0.2, 0.5, fieldN.n));
    // Seen from afar the fields only tint the land: bold patches read as paint, not flowers.
    grass = mix(grass, fieldCol, field.mul(float(1).sub(near).mul(0.25).add(0.1)));
    const speckle = smoothstep(
      mix(float(0.6), float(0.45), field),
      mix(float(0.66), float(0.52), field),
      flowerN,
    );
    grass = mix(
      grass,
      mix(flowerCol, fieldCol.mul(1.4), field.mul(0.7)),
      speckle.mul(near.mul(near)).mul(0.6),
    );
    let forest: N = mix(vec3(0.018, 0.04, 0.016), vec3(0.04, 0.072, 0.026), canopy.mul(0.5).add(0.5));
    // Autumn regions: the broadleaf share of the canopy turns orange and red.
    const autumnCanopy = mix(vec3(...AUTUMN[0]), vec3(...AUTUMN[1]), canopy.mul(0.5).add(0.5)).mul(0.55);
    const broadleaf = float(1)
      .sub(smoothstep(150, 500, h).mul(0.6))
      .mul(0.75);
    forest = mix(forest, autumnCanopy, verdantAutumn(p, ctx).mul(broadleaf));
    let rock: N = mix(vec3(0.19, 0.175, 0.155), vec3(0.36, 0.33, 0.29), strata(ctx, fineT, patchT));
    rock = rock.mul(grain.mul(0.25).add(1)).mul(fine.mul(0.15).add(1));
    const dirt = vec3(0.19, 0.145, 0.095);
    const sand = vec3(0.45, 0.41, 0.32);
    // A little under fresh snow's albedo (0.8-0.9) and matte: at real values, sunlit peaks dazzled.
    const snow = vec3(0.76, 0.785, 0.82);
    // The landform (erosion): gullies hold water, soil and snow; crests are bare and dry.
    const ridge = ridgeOf(ctx);
    const gully = smoothstep(0.1, 0.65, ridge.all.negate());
    const crest = smoothstep(0.15, 0.7, ridge.all);
    grass = mix(mix(grass, lush.mul(0.8), gully.mul(0.55)), dry, crest.mul(0.3));
    // And at the scale of whole valleys: their floors lusher (water gathers there), spurs and
    // summits drier, so the far side's valleys read in colour as well as in shade.
    grass = mix(
      mix(grass, lush, smoothstep(0.1, 0.7, ridge.relief.negate()).mul(0.35)),
      dry,
      smoothstep(0.2, 0.8, ridge.relief).mul(0.2),
    );
    let land: N = mix(grass, forest.mul(canopyCrowns(ctx, cover)), cover);
    // Bare earth where slopes start to steepen.
    land = mix(
      land,
      dirt,
      smoothstep(0.1, 0.22, slope.add(patch.mul(0.04)).add(ridge.all.mul(0.05))).mul(0.6),
    );
    // A narrow shore: a wide pale band rimmed every lake like an outline from afar.
    const sandM = float(1)
      .sub(smoothstep(1.2, 4.5, h.add(fine.mul(1.5))))
      .mul(beaches(ctx));
    land = mix(land, sand, sandM);
    const ribs = rockRibs(
      crest,
      slope,
      smoothstep(profile.snowLine - 250, profile.snowLine - 60, h.add(fine.mul(60))),
    );
    const rockM = max(
      smoothstep(0.2, 0.38, slope.add(fine.mul(0.05)).add(grain.mul(0.02)).add(ridge.all.mul(0.09))),
      ribs,
    );
    land = mix(land, rock.mul(rockStreaks(ctx, rockM)), rockM);
    // Snow doesn't hold on faces steeper than ~40°: they show banded rock instead. It lies
    // lower and on steeper ground down the gullies (couloirs), and blows off the crests.
    const snowMask = smoothstep(
      profile.snowLine - 90,
      profile.snowLine + 60,
      h.add(fine.mul(60)).add(gully.mul(70)).sub(crest.mul(40)),
    )
      .mul(float(1).sub(smoothstep(0.2, 0.34, slope.add(fine.mul(0.04)).sub(gully.mul(0.08)))))
      .mul(float(1).sub(ribs.mul(0.9)));
    land = mix(land, snow.mul(grain.mul(0.05).add(1)), snowMask);
    // Gullies are shaded and damp (their narrow ones read as dark creases), crests a little
    // sun-bleached: from across the world the drainage is drawn in light and dark.
    // And at the scale of whole valleys: hollows a little darker (their walls hide some of the
    // far side's light). Lit from straight above, it gives the far side some depth.
    land = land
      .mul(float(1).sub(gully.mul(0.14)))
      .mul(creaseShade(ctx, ridge.narrow))
      .mul(crest.mul(0.1).add(1))
      .mul(reliefShade(ridge.relief));
    const roughness = mix(mix(float(0.95), float(0.8), smoothstep(0.2, 0.38, slope)), float(0.7), snowMask);
    return { albedo: land, roughness, detail: detailWeights(sandM, rockM, snowMask) };
  },

  water: { shallow: [0.06, 0.26, 0.28], deep: [0.01, 0.05, 0.09], depthScale: 22 },
};
